import React from 'react';
import { Button } from '../components/ui/button';
import { Download, FileText, AlertCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import jsPDF from 'jspdf';
import { useLanguage } from '../i18n/pageTranslations';

const GuiaCuidadosBlefaroplastia = () => {
  const lang = useLanguage();

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    let yPosition = 20;
    
    const addText = (text, fontSize = 10, isBold = false, color = [0, 0, 0]) => {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      doc.setTextColor(color[0], color[1], color[2]);
      const lines = doc.splitTextToSize(text, maxWidth);
      
      if (yPosition + (lines.length * fontSize * 0.4) > 280) {
        doc.addPage();
        yPosition = 20;
      }
      
      doc.text(lines, margin, yPosition);
      yPosition += lines.length * fontSize * 0.4 + 3;
    };
    
    const addSection = (title) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      yPosition += 5;
      addText(title, 14, true, [45, 62, 95]);
      yPosition += 2;
    };
    
    const addSubsection = (title) => {
      addText(title, 11, true, [196, 136, 109]);
    };
    
    const addBullet = (text) => {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const lines = doc.splitTextToSize('• ' + text, maxWidth - 5);
      
      if (yPosition + (lines.length * 4) > 280) {
        doc.addPage();
        yPosition = 20;
      }
      
      doc.text(lines, margin + 3, yPosition);
      yPosition += lines.length * 4 + 2;
    };

    // Header
    doc.setFillColor(45, 62, 95);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('GUÍA DE CUIDADOS', margin, 15);
    doc.setFontSize(14);
    doc.text('Blefaroplastia - Pre y Postoperatorio', margin, 25);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Dr. Ángel Norro - Cirugía Plástica y Estética', pageWidth - margin - 60, 15);
    doc.text('Tel: 952 76 45 47 | WhatsApp: 670 506 262', pageWidth - margin - 60, 22);
    
    yPosition = 45;
    
    // Disclaimer
    doc.setFillColor(255, 243, 224);
    doc.rect(margin - 5, yPosition - 5, maxWidth + 10, 30, 'F');
    doc.setDrawColor(255, 152, 0);
    doc.rect(margin - 5, yPosition - 5, maxWidth + 10, 30, 'S');
    
    doc.setTextColor(156, 102, 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('AVISO IMPORTANTE:', margin, yPosition + 2);
    doc.setFont('helvetica', 'normal');
    const disclaimerText = 'Los productos médicos y cosméticos mencionados en este documento son genéricos y pueden no ser adecuados para todos los pacientes. Estas instrucciones generales se confirmarán y adaptarán en cada caso según las circunstancias particulares de cada paciente. Consulte siempre con su médico antes de utilizar cualquier producto.';
    const disclaimerLines = doc.splitTextToSize(disclaimerText, maxWidth);
    doc.text(disclaimerLines, margin, yPosition + 8);
    
    yPosition += 40;
    
    // Introduction
    addText('El procedimiento de blefaroplastia es muy seguro. Seguir estas instrucciones ayudará a garantizar los mejores resultados posibles.', 10, false);
    
    // ANTES DEL PROCEDIMIENTO
    addSection('ANTES DEL PROCEDIMIENTO');
    
    addSubsection('Llegada y Ayuno');
    addBullet('Acuda puntualmente a la clínica.');
    addBullet('CON SEDACIÓN: No coma ni beba nada durante las 8-10 horas previas. Puede tomar medio vaso de agua al menos 4 horas antes.');
    addBullet('SIN SEDACIÓN (solo anestesia local): No es necesario el ayuno.');
    addBullet('Si recibe sedación, deberá venir acompañado y no podrá conducir después.');
    addBullet('Durante las 24 horas posteriores a la sedación, evite manejar maquinaria o tomar decisiones importantes.');
    
    addSubsection('Preparación Personal');
    addBullet('Use ropa cómoda con botones o cremallera (evite prendas que se pasen por la cabeza).');
    addBullet('Acuda con la cara limpia, sin maquillaje, cremas ni joyas.');
    
    addSubsection('Medicamentos');
    addBullet('EVITE antiinflamatorios (ibuprofeno, aspirina, naproxeno) durante los 10 días previos.');
    addBullet('Puede tomar paracetamol si necesita aliviar molestias.');
    
    addSubsection('Artículos a Preparar en Casa');
    addBullet('Lágrimas artificiales sin conservantes en monodosis');
    addBullet('Compresas o gafas de gel frío (guardar en nevera)');
    addBullet('Suero fisiológico estéril en monodosis');
    addBullet('Infusión de manzanilla');
    addBullet('Gasas estériles');
    addBullet('Paracetamol 500 mg');
    addBullet('Povidona yodada (solución) y bastoncillos');
    addBullet('Crema para cicatrices (ej: Cicapost ISDIN)');
    
    // DESPUÉS DEL PROCEDIMIENTO
    addSection('DESPUÉS DEL PROCEDIMIENTO');
    
    addSubsection('Primeros Días en Casa');
    addBullet('Aplique compresas frías 10-15 minutos cada 2 horas los primeros 3 días.');
    addBullet('Duerma con la cabeza elevada durante 3-4 noches.');
    addBullet('Use lágrimas artificiales varias veces al día.');
    addBullet('Limpie la zona con gasas y suero fisiológico o manzanilla fría.');
    addBullet('Un leve sangrado el primer día es normal.');
    
    addSubsection('Medicación');
    addBullet('Si tiene molestias: paracetamol 500 mg cada 8 horas.');
    addBullet('Tome antibióticos solo si se los han prescrito.');
    
    addSubsection('Seguimiento');
    addBullet('Envíe foto diaria por WhatsApp los primeros días, luego cada 2 días.');
    addBullet('Los puntos se retiran aproximadamente al 9º día.');
    addBullet('Hasta entonces: evite mojar la sutura y aplique povidona yodada 1 vez/día.');
    
    addSubsection('Actividades a Evitar (mínimo 1 semana)');
    addBullet('Esfuerzos físicos y ejercicio');
    addBullet('Inclinarse bruscamente o dormir boca abajo');
    addBullet('Ambientes con polvo o humo');
    addBullet('Uso prolongado de pantallas');
    
    addSubsection('Cuidado de la Cicatriz');
    addBullet('Tras retirar puntos: aplicar crema cicatrizante 1-2 veces/día durante 2 meses.');
    addBullet('Evitar exposición directa al sol sobre la cicatriz.');
    
    // SEÑALES DE ALARMA
    addSection('CONTACTE INMEDIATAMENTE SI PRESENTA:');
    doc.setTextColor(200, 0, 0);
    addBullet('Dolor persistente o intenso');
    addBullet('Sangrado que no se detiene');
    addBullet('Visión borrosa persistente');
    addBullet('Cualquier síntoma inesperado');
    doc.setTextColor(0, 0, 0);
    
    // Contact Footer
    if (yPosition > 240) {
      doc.addPage();
      yPosition = 20;
    }
    
    yPosition += 10;
    doc.setFillColor(45, 62, 95);
    doc.rect(margin - 5, yPosition, maxWidth + 10, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('CONTACTO', margin, yPosition + 8);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Teléfono: 952 76 45 47 | WhatsApp: 670 506 262', margin, yPosition + 16);
    doc.text('Estamos a su disposición durante todo el proceso de recuperación.', margin, yPosition + 22);
    
    // Footer disclaimer
    yPosition += 35;
    doc.setTextColor(128, 128, 128);
    doc.setFontSize(8);
    const footerDisclaimer = 'Este documento tiene carácter informativo general. Las instrucciones específicas para su caso serán confirmadas por su médico. Los productos mencionados son ejemplos genéricos.';
    const footerLines = doc.splitTextToSize(footerDisclaimer, maxWidth);
    doc.text(footerLines, margin, yPosition);
    
    // Save
    doc.save('Guia_Cuidados_Blefaroplastia_DrNorro.pdf');
  };

  return (
    <div className="min-h-screen bg-cream">
      <Header />
      
      <div className="pt-24 pb-12 px-4">
        <div className="container mx-auto max-w-4xl">
          {/* Header */}
          <div className="text-center mb-12">
            <FileText className="mx-auto text-[#B8975A] mb-4" size={48} />
            <h1 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-4">
              Guía de Cuidados para Blefaroplastia
            </h1>
            <p className="text-lg text-text-secondary">
              Descarga tu guía completa de cuidados pre y postoperatorios
            </p>
          </div>

          {/* Disclaimer Card */}
          <Card className="p-6 mb-8 bg-amber-50 border-l-4 border-l-amber-500">
            <div className="flex items-start gap-4">
              <AlertCircle className="text-amber-600 flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="font-semibold text-amber-800 mb-2">Aviso Importante</h3>
                <p className="text-amber-700 text-sm">
                  Los productos médicos y cosméticos mencionados en esta guía son <strong>genéricos</strong> y pueden no ser adecuados para todos los pacientes. 
                  Estas instrucciones generales se confirmarán y adaptarán en cada caso según las <strong>circunstancias particulares</strong> de cada paciente. 
                  Consulte siempre con su médico antes de utilizar cualquier producto.
                </p>
              </div>
            </div>
          </Card>

          {/* Content Preview */}
          <Card className="p-8 mb-8">
            <h2 className="text-xl font-semibold text-[#1C1C1A] mb-6">Contenido de la Guía:</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-[#B8975A] mb-3">Antes del Procedimiento</h3>
                <ul className="space-y-2 text-sm text-text-secondary">
                  <li>• Instrucciones de ayuno (con/sin sedación)</li>
                  <li>• Preparación personal</li>
                  <li>• Medicamentos a evitar</li>
                  <li>• Lista de artículos necesarios</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-medium text-[#B8975A] mb-3">Después del Procedimiento</h3>
                <ul className="space-y-2 text-sm text-text-secondary">
                  <li>• Cuidados en los primeros días</li>
                  <li>• Medicación recomendada</li>
                  <li>• Seguimiento y retirada de puntos</li>
                  <li>• Cuidado de la cicatriz</li>
                  <li>• Actividades a evitar</li>
                  <li>• Señales de alarma</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Download Button */}
          <div className="text-center">
            <Button
              onClick={generatePDF}
              className="bg-[#B8975A] hover:bg-[#9A7D4A] text-white px-8 py-6 text-lg rounded-none shadow-lg hover:shadow-xl transition-all"
              data-testid="download-care-guide-pdf"
            >
              <Download className="mr-3" size={24} />
              Descargar Guía en PDF
            </Button>
            <p className="text-sm text-text-muted mt-4">
              El PDF se generará y descargará automáticamente
            </p>
          </div>

          {/* Contact Info */}
          <Card className="p-6 mt-8 bg-[#1C1C1A] text-white">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">¿Tiene alguna duda?</h3>
              <p className="text-white/80 mb-4">
                Estamos a su disposición para resolver cualquier consulta
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:+34952764547" className="text-white hover:text-[#B8975A] transition-colors">
                  📞 952 76 45 47
                </a>
                <a href="https://wa.me/34670506262" className="text-white hover:text-gold400 transition-colors">
                  💬 670 506 262 (WhatsApp)
                </a>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default GuiaCuidadosBlefaroplastia;
